/**
 * Ensures a minimum delay has passed since a given start time.
 * Useful for preventing UI elements (like skeleton loaders) from flashing too briefly.
 *
 * @param startTime - The timestamp when the operation started (from Date.now())
 * @param minDelayMs - The minimum delay in milliseconds (default: 300ms)
 * @returns Promise that resolves after the minimum delay has been met
 *
 * @example
 * ```typescript
 * const startTime = Date.now();
 * // ... do some async work ...
 * await ensureMinimumDelay(startTime, 500); // Ensure at least 500ms has passed
 * ```
 */
export const ensureMinimumDelay = async (
  startTime: number,
  minDelayMs: number = 271,
): Promise<void> => {
  const elapsedTime = Date.now() - startTime;
  const remainingTime = Math.max(0, minDelayMs - elapsedTime);

  if (remainingTime > 0) {
    await new Promise((resolve) => setTimeout(resolve, remainingTime));
  }
};
